import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This route is the landing page after a successful Emergent Google OAuth.
// It synchronously reads `#session_id=...` from the URL fragment, exchanges it via our
// backend, and bounces the user back to wherever they started (`?next=...`).

export default function AuthCallback() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const { t } = useI18n();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // useRef synchronously guards against React StrictMode double-invocation.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      // Parse session_id from URL hash (e.g. "#session_id=abc.def")
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      const sessionId = m ? decodeURIComponent(m[1]) : "";
      const next = params.get("next") || "/student";

      if (!sessionId) {
        setErr(t("auth.googleNoSession", "Google sign-in did not return a session. Please try again."));
        setTimeout(() => nav("/login"), 2200);
        return;
      }

      try {
        await api.post("/auth/google/session", null, { headers: { "X-Session-ID": sessionId } });
        // Re-pull /auth/me so AuthContext has fresh user data, then navigate.
        await refresh();
        // Clean the hash from the URL before navigating away.
        window.history.replaceState({}, document.title, "/auth/callback");
        nav(next, { replace: true });
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
        setTimeout(() => nav("/login"), 2500);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="auth-callback" className="min-h-screen bg-black flex items-center justify-center px-5">
      <div className="card-imperial p-10 max-w-md w-full text-center">
        <div className="overline text-[#d4af37] mb-4">Seayisyen · Google</div>
        {err ? (
          <>
            <div className="font-serif text-2xl text-white mb-2">{t("auth.googleFailedTitle", "Sign-in failed")}</div>
            <p className="text-sm text-neutral-400">{err}</p>
          </>
        ) : (
          <>
            <div className="font-serif text-2xl text-white mb-2">{t("auth.googleConnecting", "Connecting your account…")}</div>
            <p className="text-sm text-neutral-400">{t("auth.googleHold", "Hold on a moment, we're signing you in securely.")}</p>
            <div className="mt-6 inline-block">
              <div className="h-1 w-32 bg-[#1a1a1a] overflow-hidden">
                <div className="h-full w-1/2 bg-[#d4af37] animate-pulse" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
